import { Injectable, Type } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActionCreator, Store } from '@ngrx/store';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { ActionCreatorWithoutProps } from 'src/app/shared/types/action-creator.type';
import { DialogDisplayService, DialogOptions } from 'studio-components';
import { AppStore } from '../../../store/store.model';
import { getTheme } from '../../../store/ui/ui.selectors';

export type DialogEffectCreatorArgs<TData = any> = {
  initiatingActions: ActionCreator[];
  openedAction: ActionCreatorWithoutProps;
  component: Type<any>;
  data?: TData;
  dialogOptions?: DialogOptions;
};

@Injectable({
  providedIn: 'root',
})
export class DialogEffectCreatorService {
  constructor(
    private actions$: Actions,
    private dialogDisplayService: DialogDisplayService,
    private store: Store<AppStore>,
  ) {}

  /**
   * Creates an effect which displays the given component in a dialog with the currently active theme applied
   * @param options options for the effect
   * @param options.initiatingActions actions which trigger display of the dialog
   * @param options.openedAction action to dispatch when the dialog has been opened
   * @param options.component component to display in the dialog
   * @param options.data optional data to pass to the component
   * @param options.dialogOptions optional dialog options to pass to the service
   */
  createEffect<TData>(options: DialogEffectCreatorArgs<TData>) {
    const {
      initiatingActions,
      openedAction: successAction,
      component,
      data,
      dialogOptions,
    } = options;
    return createEffect(() =>
      this.actions$.pipe(
        ofType(...initiatingActions),
        withLatestFrom(this.store.select(getTheme)),
        tap(([, theme]) =>
          this.dialogDisplayService.open(component, { theme, ...dialogOptions }, data),
        ),
        map(() => successAction()),
      ),
    );
  }
}
